import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { ProposalSummary, userDataInChat, UserInChat } from '../../types/chat';
import React from 'react';

export type messageStatusValue = 'ALL' | 'READ' | 'UNREAD';

export interface chatState {
  searchText: string;
  messageStatusFilter: messageStatusValue;
  senderId?: number;
  receiverId?: number;
  proposalId?: string;
  selectedUser?: UserInChat;
  lastSeenMessageId?: string;
  selectedProposal?: ProposalSummary;
  selectedRoomId?: string;
  SelectedAllUsers?: userDataInChat[] | undefined;
  isGrouping?: boolean;
  canCancelFriendlyStage?: boolean;
  disputeId?: string;
  isClosed?: boolean;
}

const initialState: chatState = {
  messageStatusFilter: 'ALL',
  searchText: '',
  senderId: undefined,
  receiverId: undefined,
  proposalId: undefined,
  selectedUser: undefined,
  lastSeenMessageId: undefined,
  selectedProposal: undefined,
  selectedRoomId: undefined,
  disputeId: undefined,
  SelectedAllUsers: undefined,
  isGrouping: undefined,
  canCancelFriendlyStage: undefined,
  isClosed: undefined,
};

export const ChatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setMessageFilterStatus: (
      state,
      action: PayloadAction<messageStatusValue>
    ) => {
      state.messageStatusFilter = action.payload;
    },
    setSenderId: (state, action: PayloadAction<number | undefined>) => {
      state.senderId = action.payload;
    },
    setReceiverId: (state, action: PayloadAction<number | undefined>) => {
      state.receiverId = action.payload;
    },
    setProposalId: (state, action: PayloadAction<string | undefined>) => {
      state.proposalId = action.payload;
    },
    setSelectedUser: (state, action: PayloadAction<UserInChat | undefined>) => {
      state.selectedUser = action.payload;
    },
    setLastSeenMessage: (state, action: PayloadAction<string>) => {
      state.lastSeenMessageId = action.payload;
    },
    setSelectedProposal: (
      state,
      action: PayloadAction<ProposalSummary | undefined>
    ) => {
      state.selectedProposal = action.payload;
    },
    setSelectedRoomId: (state, action: PayloadAction<string>) => {
      state.selectedRoomId = action.payload;
    },
    setSelectedAllUsers: (
      state,
      action: PayloadAction<userDataInChat[] | undefined>
    ) => {
      state.SelectedAllUsers = action.payload;
    },
    setIsGrouping: (state, action: PayloadAction<boolean>) => {
      state.isGrouping = action.payload;
    },
    setCanCancelFriendlyStage: (state, action: PayloadAction<boolean>) => {
      state.canCancelFriendlyStage = action.payload;
    },
    setDisputeId: (state, action: PayloadAction<string>) => {
      state.disputeId = action.payload;
    },
    setIsClosed: (state, action: PayloadAction<boolean>) => {
      state.isClosed = action.payload;
    },
  },
});

export const {
  setSearchText,
  setMessageFilterStatus,
  setProposalId,
  setReceiverId,
  setSenderId,
  setSelectedUser,
  setLastSeenMessage,
  setSelectedProposal,
  setSelectedRoomId,
  setDisputeId,
  setCanCancelFriendlyStage,
  setIsGrouping,
  setSelectedAllUsers,
  setIsClosed,
} = ChatSlice.actions;

export const useChat = () => {
  const chatState = useSelector((state: RootState) => state.chat);
  const dispatch = useDispatch();

  //? Load state from session storage on mount to avoid hydration error
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedState = sessionStorage.getItem('chatState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        Object.entries(parsedState).forEach(([key, value]) => {
          if (value !== undefined) {
            switch (key) {
              case 'searchText':
                dispatch(setSearchText(value as string));
                break;
              case 'messageStatusFilter':
                dispatch(setMessageFilterStatus(value as messageStatusValue));
                break;
              case 'senderId':
                dispatch(setSenderId(value as number));
                break;
              case 'receiverId':
                dispatch(setReceiverId(value as number));
                break;
              case 'proposalId':
                dispatch(setProposalId(value as string));
                break;
              case 'selectedUser':
                dispatch(setSelectedUser(value as UserInChat));
                break;
              case 'lastSeenMessageId':
                dispatch(setLastSeenMessage(value as string));
                break;
              case 'selectedProposal':
                dispatch(setSelectedProposal(value as ProposalSummary));
                break;
              case 'selectedRoomId':
                dispatch(setSelectedRoomId(value as string));
                break;
              case 'disputeId':
                dispatch(setDisputeId(value as string));
                break;
              case 'SelectedAllUsers':
                dispatch(setSelectedAllUsers(value as userDataInChat[]));
                break;
              case 'isGrouping':
                dispatch(setIsGrouping(value as boolean));
                break;
              case 'canCancelFriendlyStage':
                dispatch(setCanCancelFriendlyStage(value as boolean));
                break;
              case 'isClosed':
                dispatch(setIsClosed(value as boolean));
                break;
            }
          }
        });
      }
    }
  }, []);

  // Save state to session storage on changes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log('we are here in chatstate test');
      sessionStorage.setItem('chatState', JSON.stringify(chatState));
    }
  }, [chatState]);

  return chatState;
};

export default ChatSlice.reducer;
